import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { HttpTransportType, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { getAiToken } from "@shared/externalApi";
import {
  focusConversation,
  getConversations,
  getMessages,
  getPhoneStatuses,
  markAllAsReadByConversationTypes,
  nextConversationPage,
  receiveSignalRMessageResetCount,
  receiveSignalRMessageStatusFailure,
  receiveSignalRNewConversation,
  receiveSignalRNewMessage,
  receiveSignalRNewMessageWithConversationFetch,
  setHideAi,
  setSearchOpen,
  setSearchText,
  setTab,
  setUnreadOnly,
  toggleAiFilter,
} from "../../reducers/messagingReducer";
import {
  useAiFilters,
  useConversations,
  useConversationsPage,
  useCurrentTab,
  useErrors,
  useFocusedConversation,
  useHasMore,
  useHideAi,
  useLoadingConversations,
  useMessagesPage,
  useMessagingServiceUrl,
  useSearchOpen,
  useSearchText,
  useSelectedAgentFilters,
  useSelectedMessages,
  useUnreadOnly,
} from "../../reducers/messagingReducer/selectors";
import { useCurrentUser } from "../../reducers/layoutReducer/selectors";

const shouldNotFetch = (focusedConversation) =>
  !focusedConversation.uuid || focusedConversation.uuid === "temp";

const useMessaging = () => {
  const aiFilters = useAiFilters();
  const conversations = useConversations();
  const conversationsPage = useConversationsPage();
  const currentTab = useCurrentTab();
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const errors = useErrors();
  const focusedConversation = useFocusedConversation();
  const hasMore = useHasMore();
  const hideAi = useHideAi();
  const loadingConversations = useLoadingConversations();
  const messagesPage = useMessagesPage();
  const messagingServiceUrl = useMessagingServiceUrl();
  const searchOpen = useSearchOpen();
  const searchText = useSearchText();
  const selectedAgentFilters = useSelectedAgentFilters();
  const selectedMessages = useSelectedMessages();
  const unreadOnly = useUnreadOnly();

  const agentIds = selectedAgentFilters.filter((o) => o !== "all").join(",");

  useEffect(() => {
    if (shouldNotFetch(focusedConversation)) return () => {};
    return dispatch(getMessages()).abort;
  }, [focusedConversation, messagesPage]);

  useEffect(() => {
    if (shouldNotFetch(focusedConversation)) return () => {};
    return dispatch(getPhoneStatuses()).abort;
  }, [focusedConversation]);

  useEffect(() => {
    if (searchOpen && searchText.length < 2) return () => {};
    return dispatch(getConversations()).abort;
  }, [agentIds, aiFilters, conversationsPage, currentTab, searchText, hideAi, searchOpen, unreadOnly]);

  useEffect(() => {
    let connection;
    getAiToken().then(({ token }) => {
      if (!token) return;
      connection = new HubConnectionBuilder()
        .withUrl(`${messagingServiceUrl}/MessageHub?access_token=${token}`, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
          withCredentials: true,
        })
        .withAutomaticReconnect()
        .configureLogging(process.env.NODE_ENV === "production" ? LogLevel.Error : LogLevel.Debug)
        .build();

      connection.on("ActivateConversation", (conversationUuid) => {
        connection.send("AddPersonToGroup", conversationUuid);
      });

      connection.on("CreateConversation", (conversation) => {
        connection.send("AddPersonToGroup", conversation.uuid);
        dispatch(receiveSignalRNewConversation(conversation));
      });

      connection.on("CreateConversationWithMessage", (conversation, message) => {
        connection.send("AddPersonToGroup", conversation.uuid);
        dispatch(receiveSignalRNewConversation(conversation));
        dispatch(receiveSignalRNewMessage(message));
      });

      connection.on("NewMessage", (message) => {
        dispatch(receiveSignalRNewMessageWithConversationFetch(message));
      });

      connection.on("ResetConversationUnreadMessageCount", (conversationUuid, priorCount, isChannel) => {
        dispatch(receiveSignalRMessageResetCount({ conversationUuid, priorCount, isChannel }));
      });

      connection.on("UpdateSmsOrGroupSmsConversationUserMessageStatusOnFailure", (deliveryStatus) => {
        dispatch(receiveSignalRMessageStatusFailure(deliveryStatus));
      });

      // TODO: better error messaging
      connection.start().catch(console.log);
    });

    return () => {
      // TODO: better error messaging
      connection?.stop().catch(console.log);
    };
  }, [messagingServiceUrl]);

  return {
    actions: {
      focusConversation: (conversation) => dispatch(focusConversation(conversation)),
      nextPage: () => dispatch(nextConversationPage()),
      setTab: (tab) => dispatch(setTab(tab)),
      setHideAi: () => dispatch(setHideAi(!hideAi)),
      setSearchOpen: (open) => dispatch(setSearchOpen(open)),
      setSearchText: (text) => dispatch(setSearchText(text)),
      setUnreadOnly: () => dispatch(setUnreadOnly(!unreadOnly)),
      toggleAiFilter: (filters) => dispatch(toggleAiFilter(filters)),
      markAllAsRead: () => dispatch(markAllAsReadByConversationTypes()),
    },
    aiFilters,
    conversations,
    currentTab,
    currentUser,
    errors,
    focusedConversation,
    hasMore,
    hideAi,
    loadingConversations,
    searchOpen,
    searchText,
    selectedMessages,
    unreadOnly,
  };
};

export default useMessaging;
